import { produceWithPatches, applyPatches, enablePatches } from 'immer';
import {
  HISTORY_LABEL,
  HISTORY_SCOPE,
  SPEAKER_REFERENCE_KEYS,
} from '@src/constants/history';

enablePatches();

const getAudioBlock = (block) => ({
  id: block?.id,
  text: block?.text,
  voiceCode: block?.voiceCode,
  speed: block?.speed,
  startTime: block?.startTime,
  endTime: block?.endTime,
  speakerId: block?.speakerId,
});

const BLOCK_REFERENCE_FIELDS = [
  'text',
  'translatedText',
  'voiceCode',
  'speed',
  'emphasis',
  'startTime',
  'endTime',
  'speakerId',
];

// `requestId` identifies the synthesis request that produced this block's audio
// and is what the download flow uses to fetch each block's file — it must be
// snapshotted per step, else undo/redo restores a stale requestId and the
// download resolves every block to the same (old) single file.
const BLOCK_AUDIO_FIELDS = [
  'audioLink',
  'status',
  'audioExpiredAt',
  'requestId',
];

const pickFields = (source, fields) =>
  fields.reduce((acc, field) => {
    acc[field] = source?.[field] ?? null;
    return acc;
  }, {});

const getBlockPlayStateEntry = (block) => ({
  ...pickFields(block, BLOCK_REFERENCE_FIELDS),
  ...pickFields(block, BLOCK_AUDIO_FIELDS),
});

const getOriginalBlockEntry = (originalBlock) =>
  originalBlock ? pickFields(originalBlock, BLOCK_REFERENCE_FIELDS) : null;

const mapBlocks = (blocksMap, mapper) =>
  Object.keys(blocksMap || {}).reduce((acc, blockId) => {
    acc[blockId] = mapper(blocksMap[blockId]);
    return acc;
  }, {});

const snapshotBlockPlayState = (state) =>
  mapBlocks(state?.multiBlock?.blocks, getBlockPlayStateEntry);

const snapshotOriginalBlocks = (state) =>
  mapBlocks(state?.multiBlock?.originalBlocks, getOriginalBlockEntry);

const snapshotProjectAudio = (state) => ({
  audioLinkSrt: state?.audioLinkSrt ?? null,
  audioExpiredAt: state?.audioExpiredAt ?? null,
  audioSrtExpiredAt: state?.audioSrtExpiredAt ?? null,
});

// Bundle all snapshots taken at a single moment. Callers should treat this
// as opaque and pass it back to `applyHistorySnapshots` to restore.
const captureSnapshots = (state) => ({
  blockPlayState: snapshotBlockPlayState(state),
  originalBlocks: snapshotOriginalBlocks(state),
  projectAudio: snapshotProjectAudio(state),
});

// Overlay the block-audio snapshot onto the patched state. For each
// current block, set audio fields from the snapshot entry; for blocks
// missing from the snapshot, wipe any lingering audio (the block didn't
// have audio at the restored step).
const overlayBlockPlayState = (state, snapshot) => {
  if (!snapshot) return state;
  const blocks = state.multiBlock?.blocks || {};
  const nextBlocks = mapBlocks(blocks, (block) => block);
  Object.keys(nextBlocks).forEach((blockId) => {
    const entry = snapshot[blockId];
    const block = nextBlocks[blockId];
    if (entry) {
      nextBlocks[blockId] = { ...block, ...entry };
    } else if (block?.audioLink || block?.status) {
      nextBlocks[blockId] = {
        ...block,
        audioLink: null,
        status: null,
        audioExpiredAt: null,
        requestId: null,
      };
    }
  });
  return {
    ...state,
    multiBlock: { ...state.multiBlock, blocks: nextBlocks },
  };
};

// Overlay `originalBlocks` from snapshot. Replaces reference fields per
// block and drops entries that no longer existed at the restored step.
const overlayOriginalBlocks = (state, snapshot) => {
  if (!snapshot) return state;
  const originalBlocks = state.multiBlock?.originalBlocks || {};
  const nextOriginalBlocks = {};
  Object.keys(snapshot).forEach((blockId) => {
    const entry = snapshot[blockId];
    if (entry) {
      nextOriginalBlocks[blockId] = {
        ...(originalBlocks[blockId] || {}),
        ...entry,
      };
    }
  });
  return {
    ...state,
    multiBlock: { ...state.multiBlock, originalBlocks: nextOriginalBlocks },
  };
};

const overlayProjectAudio = (state, snapshot) => {
  if (!snapshot) return state;
  return {
    ...state,
    audioLinkSrt: snapshot.audioLinkSrt ?? null,
    audioExpiredAt: snapshot.audioExpiredAt ?? null,
    audioSrtExpiredAt: snapshot.audioSrtExpiredAt ?? null,
  };
};

// Apply the three per-entry snapshots in order. Used by the project
// reducer in HISTORY_APPLY_PATCHES after `applyForward` has applied the
// Immer patches — these overlays catch what patches can't.
const applyHistorySnapshots = (
  state,
  { blockPlayStateSnapshot, originalBlocksSnapshot, projectAudioSnapshot } = {},
) => {
  let next = overlayBlockPlayState(state, blockPlayStateSnapshot);
  next = overlayOriginalBlocks(next, originalBlocksSnapshot);
  next = overlayProjectAudio(next, projectAudioSnapshot);
  return next;
};

const createPatches = (state, recipe) => {
  const [nextState, patches, inversePatches] = produceWithPatches(
    state,
    recipe,
  );
  return { nextState, patches, inversePatches };
};

const applyForward = (state, patches) => applyPatches(state, patches);
const applyInverse = (state, inversePatches) =>
  applyPatches(state, inversePatches);

const getAudioKey = (project, useOriginal = false) => {
  const blocks = useOriginal
    ? project.multiBlock?.originalBlocks || {}
    : project.multiBlock?.blocks || {};
  const blockIds = useOriginal
    ? project.multiBlock?.originalBlockIds || []
    : project.multiBlock?.blockIds || [];

  return JSON.stringify({
    audioType: useOriginal ? project.originalAudioType : project.audioType,
    blockIds,
    blocks: blockIds
      .filter((blockId) => blocks[blockId]?.text)
      .map((blockId) => getAudioBlock(blocks[blockId])),
  });
};

const isAudioCurrentForProject = (project) =>
  Boolean(project?.audioLinkSrt) &&
  getAudioKey(project) === getAudioKey(project, true);

const getProjectAudioCheckpoint = (project) => ({
  audioLinkSrt: project.audioLinkSrt ?? null,
  audioExpiredAt: project.audioExpiredAt ?? null,
  audioSrtExpiredAt: project.audioSrtExpiredAt ?? null,
  multiBlock: {
    originalBlocks: { ...(project.multiBlock?.originalBlocks || {}) },
    originalBlockIds: [...(project.multiBlock?.originalBlockIds || [])],
  },
  originalAudioType: project.originalAudioType,
  audioKey: getAudioKey(project, true),
});

// ===========================================================================
// Speaker-reference detection
//
// A "reference" to a speaker exists if a patch value (or any nested value)
// either lives at a `speakerId` / `currentFilteringSpeakerId` leaf, or sits
// inside an object whose own `speakerId` field equals the deleted id.
// ===========================================================================

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const lastPathSegmentIsSpeakerRef = (path) =>
  SPEAKER_REFERENCE_KEYS.has(path[path.length - 1]);

const isBlockTaggedWithSpeaker = (value, speakerId) =>
  isPlainObject(value) && value.speakerId === speakerId;

const valueReferencesSpeaker = (value, deletedId, path = []) => {
  if (value === deletedId && lastPathSegmentIsSpeakerRef(path)) return true;
  if (isBlockTaggedWithSpeaker(value, deletedId)) return true;

  if (isPlainObject(value)) {
    return Object.entries(value).some(([key, child]) =>
      valueReferencesSpeaker(child, deletedId, [...path, key]),
    );
  }
  if (Array.isArray(value)) {
    return value.some((child, idx) =>
      valueReferencesSpeaker(child, deletedId, [...path, idx]),
    );
  }
  return false;
};

const patchReferencesSpeaker = (patch, deletedId) =>
  'value' in patch &&
  valueReferencesSpeaker(patch.value, deletedId, patch.path);

const anyPatchReferencesSpeaker = (patches, deletedId) =>
  patches.some((p) => patchReferencesSpeaker(p, deletedId));

const entryReferencesSpeaker = (entry, deletedId) =>
  anyPatchReferencesSpeaker(entry.patches, deletedId) ||
  anyPatchReferencesSpeaker(entry.inversePatches, deletedId);

const checkpointReferencesSpeaker = (checkpoint, deletedId) => {
  const originalBlocks = checkpoint?.multiBlock?.originalBlocks;
  if (!originalBlocks) return false;
  return valueReferencesSpeaker(originalBlocks, deletedId, [
    'multiBlock',
    'originalBlocks',
  ]);
};

const stateContainsDeletedSpeaker = (state, deletedId) => {
  const blocks = state?.multiBlock?.blocks || {};
  return Object.values(blocks).some((block) => block?.speakerId === deletedId);
};

// ===========================================================================
// Timeline compaction
//
// When a speaker is deleted, drop every state on the undo/redo timeline that
// has any block bound to that speaker, then re-derive the entries that chain
// the surviving states together. The visible timeline shortens; no entry can
// ever undo or redo back into a state that holds the deleted speaker.
// ===========================================================================

/**
 * Replays a history slice to recover every project state on the timeline.
 * `states[past.length]` is always the current state; walking backwards uses
 * inverse patches, walking forwards uses normal patches.
 */
const reconstructStateTimeline = (past, future, currentState) => {
  // Walks `entries` in order from `currentState`, returning the state produced
  // after each entry is applied.
  const scanFrom = (entries, getPatches) => {
    let cursor = currentState;
    return entries.map((entry) => {
      cursor = applyPatches(cursor, getPatches(entry));
      return cursor;
    });
  };

  // For past, walk last-to-first with inverse patches, then flip so the
  // result is chronological.
  const beforeCurrent = scanFrom(
    [...past].reverse(),
    (entry) => entry.inversePatches,
  ).reverse();
  const afterCurrent = scanFrom(future, (entry) => entry.patches);

  return [...beforeCurrent, currentState, ...afterCurrent];
};

/**
 * Builds a single history entry whose forward patches transform `before` into
 * `after`. Used when chaining the surviving states back into a timeline.
 */
const buildEntryBetween = (before, after, scope, label, timestamp) => {
  const { patches, inversePatches } = createPatches(before, (draft) => {
    Object.assign(draft, after);
  });
  const beforeSnapshots = captureSnapshots(before);
  const afterSnapshots = captureSnapshots(after);
  return {
    label,
    scope,
    patches,
    inversePatches,
    audioKeyBefore: getAudioKey(before),
    audioKeyAfter: getAudioKey(after),
    blockPlayStateBefore: beforeSnapshots.blockPlayState,
    blockPlayStateAfter: afterSnapshots.blockPlayState,
    originalBlocksBefore: beforeSnapshots.originalBlocks,
    originalBlocksAfter: afterSnapshots.originalBlocks,
    projectAudioBefore: beforeSnapshots.projectAudio,
    projectAudioAfter: afterSnapshots.projectAudio,
    timestamp,
  };
};

/**
 * Returns the states that survive deletion plus the new index of the current
 * state inside the surviving subsequence.
 */
const filterTimelineSurvivors = (states, deletedId, currentIdx) => {
  const keptStates = [];
  let newCurrentIdx = 0;
  states.forEach((state, idx) => {
    if (stateContainsDeletedSpeaker(state, deletedId)) return;
    if (idx < currentIdx) newCurrentIdx += 1;
    keptStates.push(state);
  });
  return { keptStates, newCurrentIdx };
};

const chainStatesIntoEntries = (states, scope, label, timestamp) =>
  states
    .slice(0, -1)
    .map((before, i) =>
      buildEntryBetween(before, states[i + 1], scope, label, timestamp),
    );

const compactSpeakerHistory = (
  slice,
  currentState,
  deletedId,
  scope = HISTORY_SCOPE.DUBBING,
  timestamp = Date.now(),
) => {
  if (!slice) return slice;
  const past = slice.past || [];
  const future = slice.future || [];
  const checkpoint = slice.latestAudioCheckpoint;

  const entriesTouchSpeaker =
    past.some((entry) => entryReferencesSpeaker(entry, deletedId)) ||
    future.some((entry) => entryReferencesSpeaker(entry, deletedId));
  const checkpointTouchesSpeaker = checkpointReferencesSpeaker(
    checkpoint,
    deletedId,
  );

  // Fast path: nothing touches the deleted speaker, return the slice as-is.
  if (!entriesTouchSpeaker && !checkpointTouchesSpeaker) return slice;

  const survivingCheckpoint = checkpointTouchesSpeaker ? null : checkpoint;

  // Only the checkpoint was contaminated — keep entries untouched.
  if (!entriesTouchSpeaker) {
    return { ...slice, latestAudioCheckpoint: survivingCheckpoint };
  }

  const states = reconstructStateTimeline(past, future, currentState);
  const currentIdx = past.length;

  // UI guard prevents deleting a speaker any current block uses. If the
  // current state itself is dirty (defensive fallback), wipe the timeline
  // rather than rebuilding from a tainted base.
  if (stateContainsDeletedSpeaker(states[currentIdx], deletedId)) {
    return { ...slice, past: [], future: [], latestAudioCheckpoint: null };
  }

  const { keptStates, newCurrentIdx } = filterTimelineSurvivors(
    states,
    deletedId,
    currentIdx,
  );
  const newEntries = chainStatesIntoEntries(
    keptStates,
    scope,
    HISTORY_LABEL.COMPACTED_SPEAKER_CHANGE,
    timestamp,
  );

  return {
    ...slice,
    past: newEntries.slice(0, newCurrentIdx),
    future: newEntries.slice(newCurrentIdx),
    latestAudioCheckpoint: survivingCheckpoint,
  };
};

export {
  createPatches,
  applyForward,
  applyInverse,
  getAudioKey,
  isAudioCurrentForProject,
  getProjectAudioCheckpoint,
  getBlockPlayStateEntry,
  getOriginalBlockEntry,
  snapshotBlockPlayState,
  snapshotOriginalBlocks,
  snapshotProjectAudio,
  captureSnapshots,
  applyHistorySnapshots,
  isPlainObject,
  valueReferencesSpeaker,
  patchReferencesSpeaker,
  entryReferencesSpeaker,
  checkpointReferencesSpeaker,
  stateContainsDeletedSpeaker,
  reconstructStateTimeline,
  buildEntryBetween,
  compactSpeakerHistory,
};
